import { AccessTime } from '@mui/icons-material';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListSubheader,
  Stack,
  Typography,
} from '@mui/material';
import { InlineStatus } from 'components/InlineStatus';
import { RichTextContent } from 'components/RichTextContent';
import { StatItem, StatTable, getStatItemSize } from 'components/StatTable';
import { MapEntityDetailsLayout } from 'features/mapping/components/MapEntityDetailsLayout';
import { useAlert } from 'features/alerts/hooks/useAlert';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getTimeAgo } from 'shared/dates';
import { makeStyles } from 'tss-react/mui';
import { HttpStatusCode, isAxiosError } from 'axios';
import { useRequireMapLayerForRouterEntity } from 'hooks/useRequireMapLayerForRouterEntity';
import { AlertGeoEvents } from 'features/alerts/components/AlertGeoEvents';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import { getCurrentLocationAsShareUrl } from 'shared/utils';
import {
  getAlertPageTitle,
  getAlertStatus,
  getDateLabels,
  getLayerNameForAlertType,
} from './AlertDetails.utils';
import { StatusStatValue } from './StatusStatValue';
import { DateStatValue } from './DateStatValue';

const useStyles = makeStyles()((theme) => ({
  message: {
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
    lineHeight: theme.typography.body1.lineHeight,
    '& p': {
      margin: 0,
      padding: '4px 0px',
    },
  },
}));

export const AlertDetails = (): JSX.Element => {
  const { classes } = useStyles();
  const { alertId } = useParams<{ alertId: string }>();
  const { t } = useTranslation();
  const { alert, isLoading, error } = useAlert({
    id: parseInt(alertId, 10),
  });

  useRequireMapLayerForRouterEntity(getLayerNameForAlertType(alert?.type));

  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();

  if (isLoading || error || !alert) {
    const errorMessage =
      isAxiosError(error) && error.status === HttpStatusCode.NotFound
        ? t('alert.notFound')
        : undefined;

    return (
      <InlineStatus
        status={isLoading ? 'loading' : 'error'}
        errorMessage={errorMessage}
      />
    );
  }

  const {
    cardDisplayData,
    dateIssued,
    dateOnset,
    dateEnds,
    isActive,
    dateLastUpdated,
    type,
    geoEvents,
    isVisible,
  } = alert;
  const { title, subtitle, affectedArea, detailBlocks, criticalDetails } =
    cardDisplayData;

  const startDate = dateOnset ?? dateIssued;

  const status = getAlertStatus(isActive, startDate);

  const { startLabel, endLabel } = getDateLabels(status, t);

  const isWea = type === 'wea';

  const stats: StatItem[] = [
    {
      label: isWea ? t('alert.issued') : startLabel,
      value: <DateStatValue date={startDate} status={status} />,
    },
    ...(!isWea
      ? [
          {
            label: endLabel,
            value: <DateStatValue date={dateEnds} status={status} />,
          },
          {
            label: t('alert.status'),
            value: (
              <StatusStatValue
                status={status}
                startDate={dateOnset}
                endDate={dateEnds}
              />
            ),
          },
        ]
      : []),
  ];

  const canShareThisAlert =
    canShareOrCopy && alert.isActive && alert.geoEvents.length === 0;

  const pageTitle = getAlertPageTitle(type, title, t);

  const handleShare = (): void => {
    shareOrCopyUrl({
      title: pageTitle,
      url: getCurrentLocationAsShareUrl(dateLastUpdated ?? undefined),
    });
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <MapEntityDetailsLayout.Root>
        <MapEntityDetailsLayout.Header
          title={title}
          subtitle={
            subtitle ? (
              <Typography sx={{ lineHeight: '22px' }}>{subtitle}</Typography>
            ) : undefined
          }
          caption={affectedArea || undefined}
        />

        <Stack spacing={3}>
          <MapEntityDetailsLayout.Body pb={0}>
            <Stack spacing={2}>
              <StatTable
                stats={stats}
                itemSize={getStatItemSize(stats.length)}
              />

              {!isVisible && (
                <Alert severity="warning" icon={<AccessTime />}>
                  <Typography variant="body2">
                    {t('alert.notVisible')}
                  </Typography>
                </Alert>
              )}

              {dateLastUpdated && (
                <Typography variant="body2">
                  {t('common.updated')}&nbsp;
                  <b>{getTimeAgo(dateLastUpdated.toISOString())}</b>
                </Typography>
              )}
            </Stack>

            {canShareThisAlert && (
              <MapEntityDetailsLayout.Actions>
                <Button
                  fullWidth
                  color="neutral"
                  variant="outlined"
                  onClick={handleShare}
                  size="large"
                >
                  {t('common.share')}
                </Button>
              </MapEntityDetailsLayout.Actions>
            )}

            {criticalDetails && (
              <Card elevation={0}>
                <Box
                  sx={{
                    bgcolor: (theme) => theme.palette.warning.main,
                    p: 1.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography fontWeight="fontWeightBold">
                    {criticalDetails.heading}
                  </Typography>
                </Box>

                <CardContent
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.25),
                    pb: '16px !important',
                    pt: '8px !important',
                  }}
                >
                  <RichTextContent
                    content={criticalDetails.text}
                    className={classes.message}
                  />
                </CardContent>
              </Card>
            )}
          </MapEntityDetailsLayout.Body>

          {geoEvents.length > 0 && <AlertGeoEvents geoEvents={geoEvents} />}
        </Stack>

        {detailBlocks.length > 0 && (
          <List
            subheader={
              <ListSubheader variant="watchduty-section-header">
                <Typography variant="h3">{t('alert.details')}</Typography>
              </ListSubheader>
            }
            disablePadding
          >
            {detailBlocks.map((block, index) => (
              <ListItem
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`${block.heading}-${index}`}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                {block.heading && (
                  <Typography
                    variant="h4"
                    textTransform="uppercase"
                    color="textSecondary"
                  >
                    {block.heading}
                  </Typography>
                )}
                <RichTextContent
                  content={block.text}
                  className={classes.message}
                />
              </ListItem>
            ))}
          </List>
        )}
      </MapEntityDetailsLayout.Root>
    </>
  );
};
